interface LogEntry {
  id: string;
  timestamp: Date;
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  source: string;
  data?: any;
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000; // Limite de logs para evitar problemas de memória

  private addLog(level: LogEntry['level'], message: string, source: string, data?: any) {
    // Verificar configurações de logging
    const debugMode = localStorage.getItem('advanced_settings') ? 
      JSON.parse(localStorage.getItem('advanced_settings')!).debugMode : false;
    
    const logLevel = localStorage.getItem('log_level') || 'info';
    const errorReporting = localStorage.getItem('error_reporting') === 'true';

    // Verificar se deve logar baseado no nível
    const levelPriority = { debug: 0, info: 1, warn: 2, error: 3 };
    const currentLevelPriority = levelPriority[level as keyof typeof levelPriority];
    const configuredLevelPriority = levelPriority[logLevel as keyof typeof levelPriority];
    
    if (currentLevelPriority < configuredLevelPriority) {
      return; // Não logar se o nível for menor que o configurado
    }

    // Se debug mode estiver desativado, não logar debug
    if (level === 'debug' && !debugMode) {
      return;
    }

    const log: LogEntry = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
      level,
      message,
      source,
      data
    };

    this.logs.unshift(log); // Adicionar no início

    // Manter apenas os últimos maxLogs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    // Salvar no localStorage
    this.saveToStorage();

    // Log no console também
    const consoleMethod = level === 'error' ? 'error' : 
                         level === 'warn' ? 'warn' : 
                         level === 'debug' ? 'log' : 'info';
    
    console[consoleMethod](`[${level.toUpperCase()}] [${source}]: ${message}`, data || '');

    // Relatório de erros se ativado
    if (level === 'error' && errorReporting) {
      this.reportError(log);
    }
  }

  private reportError(log: LogEntry) {
    // Simular envio de relatório de erro
    console.log('📧 Enviando relatório de erro:', log);
    
    // Em produção, aqui seria enviado para um serviço de monitoramento
    // como Sentry, LogRocket, ou um webhook personalizado
  }

  private saveToStorage() {
    try {
      localStorage.setItem('system_logs', JSON.stringify(this.logs));
    } catch (error) {
      console.error('Erro ao salvar logs no localStorage:', error);
    }
  }

  private loadFromStorage() {
    try {
      const savedLogs = localStorage.getItem('system_logs');
      if (savedLogs) {
        this.logs = JSON.parse(savedLogs).map((log: any) => ({
          ...log,
          timestamp: new Date(log.timestamp)
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar logs do localStorage:', error);
      this.logs = [];
    }
  }

  debug(message: string, source: string, data?: any) {
    this.addLog('debug', message, source, data);
  }

  info(message: string, source: string, data?: any) {
    this.addLog('info', message, source, data);
  }

  warn(message: string, source: string, data?: any) {
    this.addLog('warn', message, source, data);
  }

  error(message: string, source: string, data?: any) {
    this.addLog('error', message, source, data);
  }

  getAllLogs(): LogEntry[] {
    if (this.logs.length === 0) {
      this.loadFromStorage();
    }
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
    localStorage.removeItem('system_logs');
  }

  getLogsByLevel(level: LogEntry['level']): LogEntry[] {
    return this.getAllLogs().filter(log => log.level === level);
  }

  searchLogs(query: string): LogEntry[] {
    const lowerQuery = query.toLowerCase();
    return this.getAllLogs().filter(log => 
      log.message.toLowerCase().includes(lowerQuery) ||
      log.source.toLowerCase().includes(lowerQuery)
    );
  }
}

// Instância singleton
export const logger = new Logger();

// Função helper para facilitar o uso
export const log = {
  debug: (message: string, source: string, data?: any) => logger.debug(message, source, data),
  info: (message: string, source: string, data?: any) => logger.info(message, source, data),
  warn: (message: string, source: string, data?: any) => logger.warn(message, source, data),
  error: (message: string, source: string, data?: any) => logger.error(message, source, data)
};
